import Navigation from "../components/Navigation";
import Seo from "../components/Seo";
import LogoStrip from "../components/LogoStrip";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import {
  Gauge,
  LineChart,
  ClipboardList,
  PenTool,
  Database,
  GraduationCap,
  ArrowRight,
} from "lucide-react";

// Cornerstone-/Leistungsseite: Berichtswesen + Entscheider-Dashboards (CEO/CFO/Controlling),
// auf Basis von Power BI. Schlagwoerter bewusst breit, damit auch "Power BI" greift.

const TITLE =
  "CEO-, CFO- & Controlling-Dashboards | Berichtswesen mit Power BI – Daaniyal Khan";
const DESCRIPTION =
  "Management-Dashboards und automatisiertes Berichtswesen für Geschäftsführung, CFO und Controlling: CEO- und CFO-Dashboards, Controlling- und Vertriebs-Reporting auf Basis von Power BI — von manuellen Excel-Reports zur Echtzeit-Steuerung im DACH-Raum.";

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: "Berichtswesen & Management-Dashboards (Power BI)",
  name: "CEO-, CFO- & Controlling-Dashboards und Berichtswesen mit Power BI",
  description: DESCRIPTION,
  provider: {
    "@type": "Person",
    name: "Daaniyal Khan",
    url: "https://www.daaniyalkhan.com",
    jobTitle: "Berater für Business Intelligence, Berichtswesen und Dashboards",
  },
  areaServed: ["DE", "AT", "CH"],
  audience: {
    "@type": "Audience",
    audienceType:
      "Geschäftsführung, CEO, CFO, Finanzleitung, Controlling, Vertriebsleitung",
  },
  url: "https://www.daaniyalkhan.com/management-dashboards",
};

const DASHBOARDS = [
  {
    icon: Gauge,
    title: "CEO- & Management-Dashboards",
    body: "Die wichtigsten Steuerungs-KPIs auf einen Blick — Umsatz, Wachstum, Pipeline, Zielerreichung. Eine Wahrheit für die Geschäftsführung statt zehn Excel-Versionen.",
  },
  {
    icon: LineChart,
    title: "CFO- & Finanz-Dashboards",
    body: "Liquidität, GuV, Deckungsbeiträge und Forecast in Echtzeit. Finanz-Reporting, das Entscheidungen trägt statt nur den Monat zu dokumentieren.",
  },
  {
    icon: ClipboardList,
    title: "Controlling- & Vertriebs-Reporting",
    body: "Automatisiertes Berichtswesen für Controlling und Vertriebssteuerung — von Soll-Ist und Abweichungsanalyse bis zur Rennliste je Einheit.",
  },
];

const FORMATS = [
  {
    icon: PenTool,
    title: "Dashboard-Konzeption",
    body: "Welche Kennzahlen steuern wirklich? Wir definieren die richtigen KPIs und das Zielbild — bevor das erste Diagramm entsteht.",
  },
  {
    icon: Database,
    title: "Aufbau mit Power BI",
    body: "Datenanbindung, Datenmodell und Dashboards in Power BI — sauber, performant und wartbar, mit einer belastbaren Single Source of Truth.",
  },
  {
    icon: ClipboardList,
    title: "Automatisiertes Berichtswesen",
    body: "Wiederkehrende Berichte laufen automatisch statt manuell — weniger Excel-Handarbeit, weniger Fehler, immer aktuell.",
  },
  {
    icon: GraduationCap,
    title: "Schulung & Self-Service",
    body: "Ihr Team wird befähigt, Dashboards selbst zu lesen und weiterzuentwickeln — damit das Reporting nicht an einer Person hängt.",
  },
];

const FAQ = [
  {
    q: "Warum Power BI?",
    a: "Power BI verbindet sich mit nahezu jeder Datenquelle, skaliert vom Team bis zum Konzern und ist im Microsoft-Umfeld der meisten Unternehmen bereits lizenziert. Es ist die pragmatische Basis für belastbares Berichtswesen — die Methode (richtige KPIs, saubere Daten) zählt aber mehr als das Werkzeug.",
  },
  {
    q: "Wir berichten schon in Excel — wozu Dashboards?",
    a: "Manuelle Excel-Reports sind aufwendig, fehleranfällig und immer einen Schritt zu spät. Ein automatisiertes Dashboard liefert dieselben Zahlen in Echtzeit, konsistent und ohne Copy-Paste — und setzt Controlling-Kapazität für Analyse frei.",
  },
  {
    q: "Geht es nur um Finanzen?",
    a: "Nein. Schwerpunkt sind CEO-, CFO- und Controlling-Dashboards, aber dasselbe Prinzip trägt auch Vertriebs-, Service- und Operations-Reporting.",
  },
];

const ManagementDashboards = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Seo title={TITLE} description={DESCRIPTION} />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-[#C5A66A] focus:text-[#0F1B2D] focus:px-4 focus:py-2 focus:rounded focus:font-semibold"
      >
        Zum Hauptinhalt springen
      </a>
      <Navigation />
      <main id="main-content" className="pt-32 pb-20">
        <div className="container mx-auto px-6 max-w-4xl">
          <header className="mb-16 max-w-3xl">
            <div className="text-sm text-[#C5A66A] uppercase tracking-wider font-semibold mb-4">
              Berichtswesen & Dashboards · Power BI · DACH
            </div>
            <h1 className="font-playfair text-4xl lg:text-6xl font-bold text-[#0F1B2D] mb-6 leading-tight">
              CEO-, CFO- & Controlling-Dashboards — Berichtswesen, das steuert
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Die meisten Unternehmen ertrinken in Berichten und sehen trotzdem
              die entscheidenden Zahlen zu spät. Ich baue Management-Dashboards
              und automatisiertes Berichtswesen — von der KPI-Definition bis zum
              fertigen Dashboard auf Basis von Power BI. Aus manuellen
              Excel-Reports wird Echtzeit-Steuerung für Geschäftsführung, CFO und
              Controlling.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/#contact"
                className="inline-flex items-center bg-[#C5A66A] text-[#0F1B2D] px-7 py-3 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
              >
                Strategie-Gespräch buchen
                <ArrowRight size={18} className="ml-2" />
              </Link>
            </div>
          </header>

          {/* Für wen */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-4">
              Für wen
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Geschäftsführung und CEOs, CFOs und Finanzleitung, Controlling und
              Vertriebsleitung — im Mittelstand wie in größeren Organisationen.
              Überall dort, wo Entscheidungen auf Zahlen beruhen sollen, die
              aktuell, konsistent und verständlich sind.
            </p>
          </section>

          {/* Was ich baue */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-8">
              Was ich baue
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {DASHBOARDS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="bg-[#FAFAF8] rounded-2xl border border-gray-200 p-6"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#C5A66A]/10 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#C5A66A]" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-lg text-[#0F1B2D] mb-2">{title}</h3>
                  <p className="text-gray-600 leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Von Excel zu Echtzeit */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-4">
              Von Excel-Handarbeit zur Echtzeit-Steuerung
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Typischer Ausgangspunkt: Berichte werden monatlich von Hand aus
              mehreren Quellen in Excel zusammengetragen — aufwendig, fehleranfällig
              und immer einen Monat zu spät. Zielbild: eine saubere Datenbasis,
              ein klares KPI-Set und Dashboards, die sich selbst aktualisieren.
              Entscheider sehen ihre Zahlen jederzeit, das Controlling gewinnt Zeit
              für Analyse statt Zusammenkopieren — und alle reden über dieselben
              Werte.
            </p>
          </section>

          {/* Aus der Praxis: CFO & CEO Dashboards */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-4">
              Aus der Praxis: CFO- & CEO-Dashboards für Versicherung & Vertrieb
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Zwei Dashboards aus derselben, sauberen Datenbasis — auf zwei
              Ebenen gedacht: das CFO-Dashboard als Controlling-Instrument in der
              Tiefe, das CEO-Dashboard als Führungs- und Steuerungsinstrument in
              der Verdichtung.
            </p>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="bg-[#FAFAF8] rounded-2xl border border-gray-200 p-7">
                <div className="w-12 h-12 rounded-xl bg-[#C5A66A]/10 flex items-center justify-center mb-4">
                  <LineChart size={24} className="text-[#C5A66A]" aria-hidden="true" />
                </div>
                <h3 className="font-bold text-lg text-[#0F1B2D] mb-2">
                  CFO-Dashboard — Controlling-Instrument
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Alle steuerungsrelevanten Zahlen für Versicherung und Vertrieb
                  auf einer Fläche: GuV und Deckungsbeiträge, Combined Ratio und
                  Schadenquote, Storno- und Bestandsentwicklung, Neugeschäft,
                  Provisions- und Kostenquoten sowie Liquidität — als Soll-Ist
                  mit Abweichungsanalyse und Forecast. Tiefe und Granularität
                  fürs Controlling.
                </p>
              </div>
              <div className="bg-[#0F1B2D] rounded-2xl p-7 text-white">
                <div className="w-12 h-12 rounded-xl bg-[#C5A66A]/15 flex items-center justify-center mb-4">
                  <Gauge size={24} className="text-[#C5A66A]" aria-hidden="true" />
                </div>
                <h3 className="font-bold text-lg text-[#C5A66A] mb-2">
                  CEO-Dashboard — Führungs- & Steuerungsinstrument
                </h3>
                <p className="text-gray-200 leading-relaxed">
                  Dieselbe Datenbasis, verdichtet auf die Führungssicht: Wachstum
                  und Zielerreichung, Profitabilität, Vertriebsleistung und
                  Frühwarn-Indikatoren auf einen Blick — weniger Detail, mehr
                  Entscheidung. Bei Bedarf ein Klick in die Tiefe des
                  CFO-Cockpits.
                </p>
              </div>
            </div>
            <p className="mt-6 text-xs text-gray-400">
              Anonymisiert. Details auf Anfrage.
            </p>
          </section>

          {/* Formate */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-8">
              So arbeiten wir
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {FORMATS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="flex gap-4 bg-white rounded-2xl border border-gray-200 p-6"
                >
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-[#C5A66A]/10 flex items-center justify-center">
                    <Icon size={24} className="text-[#C5A66A]" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-[#0F1B2D] mb-1">
                      {title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">{body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Partner & Referenzen */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-6">
              Partner &amp; Referenzen
            </h2>
            <LogoStrip />
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="font-playfair text-3xl font-bold text-[#0F1B2D] mb-8">
              Häufige Fragen
            </h2>
            <div className="space-y-6">
              {FAQ.map(({ q, a }) => (
                <div key={q}>
                  <h3 className="font-bold text-lg text-[#0F1B2D] mb-2">{q}</h3>
                  <p className="text-gray-600 leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-[#0F1B2D] to-[#1a2d4a] rounded-2xl p-10 text-white text-center">
            <h2 className="text-3xl font-bold mb-4 text-[#C5A66A]">
              Welche Zahl steuert Ihr Geschäft?
            </h2>
            <p className="text-lg mb-8 max-w-2xl mx-auto">
              Ein 90-Minuten-Gespräch klärt, welche Dashboards Ihre
              Geschäftsführung wirklich braucht — und wie das Berichtswesen
              dorthin kommt.
            </p>
            <Link
              to="/#contact"
              className="inline-block bg-[#C5A66A] text-[#0F1B2D] px-8 py-4 rounded-lg font-semibold hover:bg-[#b8966b] hover:scale-105 transition-all duration-300"
            >
              Kostenloses Erstgespräch
            </Link>
          </section>
        </div>
      </main>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
      />
    </div>
  );
};

export default ManagementDashboards;
